import React from "react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";

function activeOrgFromUser(user) {
  return (
    user?.currentOrgId ||
    user?.currentOrg ||
    user?.effectiveOrgSlug ||
    user?.effectiveOrg ||
    ""
  );
}

function normalizePdfRequestUrl(input, org) {
  if (!input) return "";
  const raw = String(input);
  const apiBaseRaw = (api.defaults?.baseURL || process.env.REACT_APP_API_BASE || window.location.origin || "").replace(/\/$/, "");

  try {
    const origin = window.location.origin || "http://localhost";
    const apiBase = new URL(apiBaseRaw || origin, origin);
    const parsed = new URL(raw, apiBase);
    if (org && !parsed.searchParams.get("org")) {
      parsed.searchParams.set("org", org);
    }

    if (parsed.origin === apiBase.origin) {
      const basePath = apiBase.pathname.replace(/\/$/, "");
      let pathname = parsed.pathname;
      if (basePath && basePath !== "/" && pathname.startsWith(`${basePath}/`)) {
        pathname = pathname.slice(basePath.length) || "/";
      } else if (basePath && basePath !== "/" && pathname === basePath) {
        pathname = "/";
      }
      return `${pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch (_) {
    if (!org || raw.includes("org=")) return raw;
    const joiner = raw.includes("?") ? "&" : "?";
    return `${raw}${joiner}org=${encodeURIComponent(org)}`;
  }
}

export default function PdfViewer({ url, height = 700 }) {
  const { user } = useAuth();
  const org = activeOrgFromUser(user);
  const requestUrl = React.useMemo(() => normalizePdfRequestUrl(url, org), [url, org]);
  const [blobUrl, setBlobUrl] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    let localBlobUrl = "";

    const loadPdf = async () => {
      if (!requestUrl) {
        setBlobUrl("");
        setError("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      setBlobUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return "";
      });

      try {
        const response = await api.get(requestUrl, { responseType: "blob" });
        const contentType = response.headers?.["content-type"] || "application/pdf";
        const blob = response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: contentType });
        localBlobUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setBlobUrl(localBlobUrl);
        } else {
          URL.revokeObjectURL(localBlobUrl);
        }
      } catch (err) {
        if (!cancelled) {
          setError("PDF could not be loaded. Please reopen the record or sign in again if your session expired.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
    };
  }, [requestUrl]);

  React.useEffect(() => () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  return (
    <div style={{ border: "1px solid #e6e6e6", borderRadius: 8, overflow: "hidden" }}>
      {loading ? <div style={{ padding: 12 }}>Loading PDF…</div> : null}
      {!loading && error ? <div style={{ padding: 12 }}>{error}</div> : null}
      {!loading && !error && blobUrl ? (
        <iframe title="pdf" src={blobUrl} style={{ width: "100%", height, border: 0 }} />
      ) : null}
    </div>
  );
}
